import PropTypes from 'prop-types';

import WhiteCard from '../../../../components/WhiteCard';
import { shortMonthDateString } from '../../../../lib/dateFormatter';
import OfferDetailsSection from '../OfferDetailsSection';
import PromotionalRateInfoBox from '../PromotionalRateInfoBox';

import './index.scss';

const InterestRate = ({
  explanationOpen,
  margin,
  baseRate,
  interestRate,
  term,
  disbursementDate,
  offerIssuedDuringPromoPeriod,
}) => {
  const formattedDisbursementDate = disbursementDate
    ? shortMonthDateString(new Date(disbursementDate))
    : null;

  const explanationText = (
    <>
      <p>
        The interest rate uses a simple interest rate formula, meaning it does not compound. It only
        starts to accrue once the loan is disbursed
        {formattedDisbursementDate ? `, estimated to be on ${formattedDisbursementDate}` : ''}.
      </p>
      <p>
        The interest rate is made up of 2 components: a fixed portion and a variable portion. The
        variable portion is based on 30- day average SOFR, a publicly available rate that is widely
        used in financial markets as a benchmark.
      </p>
    </>
  );

  return (
    <OfferDetailsSection
      className="interest-rate-v3"
      explanationOpen={explanationOpen}
      title="Interest rate"
      explanationText={explanationText}
    >
      {offerIssuedDuringPromoPeriod && <PromotionalRateInfoBox />}
      <div className="interest-rate-v3__fixed-rate__wrapper">
        <WhiteCard className="interest-rate-v3__white-card">
          <div className="interest-rate-v3__wrapper interest-rate-v3__fixed">
            <div className="interest-rate-v3__value h2" data-testid="fixed-rate-value">
              {margin}%
            </div>
            <div className="interest-rate-v3__label h6" data-testid="fixed-rate-label">
              <strong>Fixed</strong>
              <span>(for {term}&nbsp;years loan term)</span>
            </div>
          </div>
        </WhiteCard>
      </div>
      <div className="interest-rate-v3__separator">+</div>
      <div className="interest-rate-v3__container">
        <WhiteCard className="interest-rate-v3__white-card">
          <div className="interest-rate-v3__wrapper interest-rate-v3__variable">
            <div className="interest-rate-v3__variable__value">
              <div className="interest-rate-v3__value h2" data-testid="variable-rate-value">
                {baseRate}%
              </div>
              <div className="interest-rate-v3__label h6" data-testid="variable-rate-label">
                <strong>Variable</strong>
                <span>(updated every month)</span>
              </div>
            </div>
          </div>
          <div className="interest-rate-v3__graph__wrapper">
            <div className="interest-rate-v3__graph" data-testid="interest-rate-v3-graph"></div>
          </div>
          <div className="interest-rate-v3__sofr__wrapper">
            <div className="interest-rate-v3__sofr h6" data-testid="variable-rate-sofr">
              <span>
                Source:&nbsp;
                <a href="https://prodigyfinance.com/legal/sofr/" target="_blank" rel="noreferrer">
                  30-Day Average SOFR
                </a>
              </span>
              <span>Last update: Apr 2025</span>
            </div>
          </div>
        </WhiteCard>
      </div>
      <div className="interest-rate-v3__separator">=</div>
      <WhiteCard className="interest-rate-v3__white-card">
        <div className="interest-rate-v3__wrapper interest-rate-v3__total">
          <div className="interest-rate-v3__value h2" data-testid="total-rate-value">
            {interestRate}%
          </div>
          <div className="interest-rate-v3__label h6" data-testid="total-rate-label">
            <strong>Interest rate</strong>
          </div>
          <div className="interest-rate-v3__label h6">
            <span>(as of 6 Apr 2025)</span>
          </div>
        </div>
      </WhiteCard>
    </OfferDetailsSection>
  );
};

InterestRate.propTypes = {
  explanationOpen: PropTypes.bool.isRequired,
  margin: PropTypes.string.isRequired,
  baseRate: PropTypes.string.isRequired,
  interestRate: PropTypes.string.isRequired,
  term: PropTypes.number.isRequired,
  disbursementDate: PropTypes.string,
  offerIssuedDuringPromoPeriod: PropTypes.bool.isRequired,
};

export default InterestRate;
